import { Box, Fade, Typography } from '@mui/material';
import { RenterInviteTextInput } from './components';
import { Control, Controller, FieldErrors, UseFormWatch } from 'react-hook-form';
import GroupRadioButtonV2, { type RadioInputBox } from '../../components/input/GroupRadioButtonV2';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import { CurrencyFixedDecimalInput } from '@/shared/formatters';
import { Renter } from '../../utils/types/renter.interface';

const rentOptions: RadioInputBox[] = [
  {
    id: 'rent-coverage-company-default',
    text: "My company's default",
    value: RentCoverageTypes.CompanyDefault,
  },
  {
    id: 'rent-coverage-customize',
    text: 'Customize Rent Coverage',
    value: RentCoverageTypes.Customize,
  },
];

const securityDepositOptions: RadioInputBox[] = [
  {
    id: 'security-deposit-coverage-company-default',
    text: "My company's default",
    value: SecurityDepositTypes.CompanyDefault,
  },
  {
    id: 'security-deposit-coverage-customize-or-remove',
    text: 'Customize or remove Deposit Coverage',
    value: SecurityDepositTypes.CustomizeOrRemove,
  },
];

interface CoverageSelectionV2Props {
  control: Control<Renter, any>;
  watch: UseFormWatch<Renter>;
  errors: FieldErrors<Renter>;
  useDrawerStyles?: boolean;
  selfEnrolledSDRLimit?: number;
}

const CoverageSelectionV2 = ({
  control,
  watch,
  errors,
  useDrawerStyles = false,
  selfEnrolledSDRLimit,
}: CoverageSelectionV2Props) => {
  const seeRentCoverage = watch('rentCoverage');
  const seeSecurityDepositCoverage = watch('securityDepositCoverage');

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          component="h2"
          fontWeight="bold !important"
          lineHeight="28px"
          mb={1}
        >
          Coverage selection
        </Typography>
        <Typography
          variant="body2"
          fontSize="16px !important"
          fontWeight="350"
          mb={4}
          letterSpacing={0}
        >
          You may set coverage requirements that apply to this deal only, rather than relying on
          your company's default settings.
        </Typography>
        <Typography
          variant="body2"
          fontSize="16px !important"
          fontWeight="350"
          mb="12px"
          letterSpacing={0}
        >
          Rent Coverage
        </Typography>
        <Controller
          control={control}
          name="rentCoverage"
          render={({ field }) => (
            <GroupRadioButtonV2
              field={field}
              id="rent-coverage"
              dataTestId="rent-coverage"
              options={rentOptions}
              useDrawerStyles={useDrawerStyles}
            />
          )}
        />
        {seeRentCoverage === RentCoverageTypes.Customize && (
          <Fade in={seeRentCoverage === RentCoverageTypes.Customize}>
            <Box>
              <Controller
                control={control}
                name="monthsOfCoverage"
                defaultValue={undefined}
                render={({ field }) => (
                  <RenterInviteTextInput
                    {...field}
                    ref={null}
                    size="small"
                    label="Months of coverage"
                    id="months-of-coverage"
                    type="number"
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    error={!!errors.monthsOfCoverage}
                    helperText={
                      errors.monthsOfCoverage?.message?.toString() ||
                      "Please enter the number of months you'd like covered excluding prepaid rent, concessions and security deposit."
                    }
                    required
                  />
                )}
              />
            </Box>
          </Fade>
        )}
      </Box>
      <Box>
        <Typography
          variant="body2"
          fontSize="16px !important"
          fontWeight="350"
          mb="12px"
          letterSpacing={0}
        >
          Security Deposit Coverage
        </Typography>
        <Controller
          name="securityDepositCoverage"
          control={control}
          render={({ field }) => (
            <GroupRadioButtonV2
              field={field}
              id="security-deposit-coverage"
              dataTestId="security-deposit-coverage"
              options={securityDepositOptions}
              useDrawerStyles={useDrawerStyles}
            />
          )}
        />
        {seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove && (
          <Fade in={seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove}>
            <Box>
              <Controller
                control={control}
                name="securityDepositReplacementAmount"
                render={({ field }) => (
                  <RenterInviteTextInput
                    {...field}
                    ref={null}
                    size="small"
                    id="security-deposit-replacement-amount"
                    InputProps={{
                      inputComponent: CurrencyFixedDecimalInput,
                      disableUnderline: true,
                    }}
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    label="Security deposit replacement amount"
                    error={!!errors.securityDepositReplacementAmount}
                    helperText={
                      errors.securityDepositReplacementAmount?.message?.toString() ||
                      `If you are allowing your renter to replace a cash deposit, please enter the dollar amount.${
                        selfEnrolledSDRLimit
                          ? ` Value cannot exceed the equivalent of ${selfEnrolledSDRLimit} month's rent.`
                          : ''
                      }`
                    }
                    required
                  />
                )}
              />
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );
};

export default CoverageSelectionV2;
